import { useRef } from 'react';
import './style.scss';
import { IdHelper } from '../../../../../helpers/id.helper';
import type { ITrafficSituation } from '../../../../../models/traffic-situation.model';
import { TrafficSituationHelper } from '../../../../../helpers/traffic-situation.helper';
import { AnalyticsService } from '../../../../../services/analytics-service';
import EpiserverHelper from '../../../../../helpers/episerver.helper';
import TrafficSituationTag from '../../../components/Tag';
import LineListFact from '../../../components/facts/LineListFact';
import SeverityAndTitleFact from '../../../components/facts/SeverityAndTitleFact';
import ValidityPeriodFact from '../../../components/facts/ValidityPeriodFact';
import { t } from '../../../../../services/texts.service';
import MunicipalitiesFact from '../../../components/facts/MunicipalitiesFact';
import AffectedPeriodsFact from '../../../components/facts/AffectedPeriodsFact';
import { AffectedPeriodsFactHelper } from '../../../components/facts/AffectedPeriodsFact/affected-periods-fact.helper';

interface ITrafficSituationV2Texts {
  linkToDetailsPage: string;
}

// === EVENT HANDLERS ===
function handleShowMoreDetailsClick() {
  AnalyticsService.addEvent(AnalyticsService.TrafficSituations, 'Klick på visa mer information', '');
}

function handleExpandTrafficSituationDetails() {
  AnalyticsService.addEvent(AnalyticsService.TrafficSituations, 'Visa störningsmeddelande-detaljer', '');
}

function TrafficSituationV2({ trafficSituation, texts }: { trafficSituation: ITrafficSituation; texts: ITrafficSituationV2Texts }) {
  // VARIABLES
  const trafficSituationRef = useRef<HTMLHeadingElement>(null);
  const id = IdHelper.newUniqueId('traffic-situation-collapsible-');
  const isNewTrafficSituation = TrafficSituationHelper.isNewTrafficSituation(trafficSituation);
  const affectedMunicipalities = TrafficSituationHelper.getAffectedMunicipalities(trafficSituation);
  const affectedPeriods = AffectedPeriodsFactHelper.getFormattedPeriods(trafficSituation.activeTimes);

  // RENDER
  return (
    <div className="accordion-item traffic-situation-two-dot-zero p-2 p-sm-3 print-no-break bg-white border rounded position-relative" ref={trafficSituationRef}>
      {EpiserverHelper.isInEditMode() && <span className="fw-bold">{trafficSituation.situationNumber}</span>}
      {isNewTrafficSituation && (
        <div className="traffic-situation-two-dot-zero__tags d-flex gap-1">
          <TrafficSituationTag text={t('Ny', 'New')} />
        </div>
      )}
      <a
        className="accordion-button p-0 traffic-situation-two-dot-zero__collapse-button collapsed text-decoration-none"
        data-bs-toggle="collapse"
        href={`#${id}`}
        role="button"
        aria-expanded="false"
        aria-controls={id}
        onClick={handleExpandTrafficSituationDetails}
      >
        <div className="d-flex flex-column flex-grow-1 gap-2">
          <SeverityAndTitleFact trafficSituation={trafficSituation} />
          <LineListFact trafficSituation={trafficSituation} />
          <ValidityPeriodFact trafficSituation={trafficSituation} />
        </div>
      </a>
      <div className="collapse" id={id}>
        <div className={'d-flex flex-column gap-2 pt-2'}>
          {affectedPeriods.length > 0 && <AffectedPeriodsFact trafficSituation={trafficSituation} />}
          <MunicipalitiesFact municipalities={affectedMunicipalities} />
          {trafficSituation.description && <div>{trafficSituation.description}</div>}
          {trafficSituation.relativeDetailsUrl && (
            <div>
              <a href={trafficSituation.relativeDetailsUrl} onClick={handleShowMoreDetailsClick}>
                {texts.linkToDetailsPage}
              </a>
            </div>
          )}
          {affectedPeriods.length === 0 && affectedMunicipalities.length === 0 && !trafficSituation.description && !trafficSituation.relativeDetailsUrl && (
            <div>{t('Ingen ytterligare information finns tillgänglig för denna störning.', 'No additional information is available for this disruption.')}</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TrafficSituationV2;
